// Spotify App Opener Utility
// Handles opening Spotify app without browser fallback when app opens successfully

export class SpotifyAppOpener {
  static openSpotifyApp(spotifyUrl, song = '', artist = '', options = {}) {
    if (!spotifyUrl || typeof spotifyUrl !== 'string' || spotifyUrl.trim() === '') {
      console.warn('Invalid Spotify URL provided:', spotifyUrl);
      return;
    }
    
    const {
      showNotification = true,
      fallbackToWeb = false, // Default to false to prevent dual opening
      fallbackDelay = 2000
    } = options;
    
    // Convert web URL to Spotify URI for app deep linking
    let spotifyUri = spotifyUrl;
    if (spotifyUrl.includes('open.spotify.com')) {
      spotifyUri = spotifyUrl
        .replace('https://open.spotify.com/', 'spotify:')
        .replace(/\//g, ':');
    }
    
    console.log('🎵 Opening Spotify app with URI:', spotifyUri);
    
    // Track if user navigated away (app opened)
    let userLeftPage = false;
    let fallbackTimer = null;
    
    // Listen for page visibility changes (more reliable than focus)
    const visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        userLeftPage = true;
        if (fallbackTimer) {
          clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }
        console.log('✅ User left page - Spotify app likely opened');
      }
    };
    
    // Listen for page blur (backup detection)
    const blurHandler = () => {
      userLeftPage = true;
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      console.log('✅ Page lost focus - Spotify app likely opened');
    };
    
    document.addEventListener('visibilitychange', visibilityHandler);
    window.addEventListener('blur', blurHandler);
    
    // Try to open in Spotify app
    try {
      window.location.href = spotifyUri;
    } catch (error) {
      console.error('Failed to open Spotify URI:', error);
      if (fallbackToWeb) {
        window.open(spotifyUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    
    // Only set up fallback if explicitly requested
    if (fallbackToWeb) {
      fallbackTimer = setTimeout(() => {
        // Clean up listeners
        document.removeEventListener('visibilitychange', visibilityHandler);
        window.removeEventListener('blur', blurHandler);
        
        if (!userLeftPage) {
          console.log('⚠️  App didn\'t open, falling back to web');
          window.open(spotifyUrl, '_blank', 'noopener,noreferrer');
        } else {
          console.log('✅ App opened successfully, no web fallback needed');
        }
      }, fallbackDelay);
    } else {
      // Clean up listeners after a short delay
      setTimeout(() => {
        document.removeEventListener('visibilitychange', visibilityHandler);
        window.removeEventListener('blur', blurHandler);
      }, 1000);
    }
    
    // Show notification if requested
    if (showNotification) {
      this.showSpotifyNotification(song, artist);
    }
  }
  
  static showSpotifyNotification(song, artist) {
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-3 rounded-lg shadow-xl z-50 transition-all duration-300 transform translate-x-0';
    notification.innerHTML = `
      <div class="flex items-center">
        <svg class="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
        </svg>
        <span class="font-medium">Opening in Spotify...</span>
      </div>
      ${song && artist ? `<div class="text-sm mt-1 opacity-90">"${song}" by ${artist}</div>` : ''}
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.transform = 'translateX(400px)';
      notification.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }
}

// Global function for backward compatibility
window.openSpotifyAppOnly = (spotifyUrl, song, artist) => {
  SpotifyAppOpener.openSpotifyApp(spotifyUrl, song, artist, {
    fallbackToWeb: false // App only, no web fallback
  });
};

window.openSpotifyWithFallback = (spotifyUrl, song, artist) => {
  SpotifyAppOpener.openSpotifyApp(spotifyUrl, song, artist, {
    fallbackToWeb: true // Allow web fallback if app doesn't open
  });
};
